// src/pages/artist/ShopOrders.jsx
import React, { useState, useEffect } from 'react';
import { getMyOrders, approveOrder, rejectOrder, shipOrder, completeOrder } from '../../services/marketplaceService';
import DashboardLayout from '../../components/layout/DashboardLayout';

const STATUS_COLORS = {
  pending_approval: { bg: '#f59e0b33', fg: '#f59e0b' },
  approved: { bg: '#3b82f633', fg: '#3b82f6' },
  rejected: { bg: '#ef444433', fg: '#ef4444' },
  paid: { bg: '#8b5cf633', fg: '#8b5cf6' },
  shipped: { bg: '#06b6d433', fg: '#06b6d4' },
  completed: { bg: '#10b98133', fg: '#10b981' },
  cancelled: { bg: '#6b728033', fg: '#6b7280' },
};

const STATUS_LABELS = {
  pending_approval: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const FILTER_TABS = [
  { value: '', label: 'All' },
  { value: 'pending_approval', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'completed', label: 'Completed' },
];

export default function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchOrders = async () => {
    try {
      const res = await getMyOrders(filter || undefined);
      setOrders(res?.data?.data?.orders || []);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load orders.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); fetchOrders(); }, [filter]);

  const handleAction = async (actionFn, orderId, label) => {
    if (!window.confirm(`${label} this order?`)) return;
    setActionLoading(orderId);
    setMessage(null);
    try {
      await actionFn(orderId);
      setMessage({ type: 'success', text: `Order ${label.toLowerCase()}d.` });
      fetchOrders();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed to ${label.toLowerCase()}.` });
    } finally {
      setActionLoading(null);
    }
  };

  const getActions = (order) => {
    const actions = [];
    const s = order.status;
    if (s === 'pending_approval') {
      actions.push({ label: 'Approve', fn: approveOrder, color: '#10b981' });
      actions.push({ label: 'Reject', fn: rejectOrder, color: '#ef4444' });
    }
    if (s === 'paid') {
      actions.push({ label: 'Ship', fn: shipOrder, color: '#3b82f6' });
    }
    if (s === 'shipped') {
      actions.push({ label: 'Complete', fn: completeOrder, color: '#7c3aed' });
    }
    return actions;
  };

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '1rem' }}>📦 Shop Orders</h1>

        {message && (
          <div style={{
            padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem',
            background: message.type === 'error' ? '#ef444433' : '#10b98133',
            color: message.type === 'error' ? '#ef4444' : '#10b981',
          }}>
            {message.text}
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              style={{
                padding: '0.4rem 1rem', borderRadius: '2rem', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.85rem',
                background: filter === tab.value ? '#7c3aed' : '#1a1a2e',
                color: filter === tab.value ? '#fff' : '#aaa',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>No orders found.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {orders.map((order) => {
              const sc = STATUS_COLORS[order.status] || STATUS_COLORS.cancelled;
              const actions = getActions(order);
              return (
                <div key={order.order_id} style={{ background: '#1a1a2e', borderRadius: '0.75rem', padding: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Order #{order.order_id?.slice(0, 8)}</div>
                      <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{order.customer_name}</div>
                      <div style={{ color: '#aaa', fontSize: '0.85rem' }}>{order.customer_email}</div>
                      {order.customer_phone && <div style={{ color: '#888', fontSize: '0.8rem' }}>{order.customer_phone}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '1rem',
                        background: sc.bg, color: sc.fg, fontWeight: 600, fontSize: '0.75rem',
                      }}>
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                      <div style={{ marginTop: '0.3rem', fontWeight: 700, fontSize: '1.1rem', color: '#10b981' }}>
                        ${order.price?.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        Sale
                      </div>
                    </div>
                  </div>

                  {/* Order Details Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #222' }}>
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>
                      Art: {order.art_id?.slice(0, 8)}… &nbsp;|&nbsp;
                      Net: <span style={{ color: '#10b981' }}>${order.artist_net?.toFixed(2)}</span> &nbsp;|&nbsp;
                      Commission: ${order.platform_commission?.toFixed(2)} &nbsp;|&nbsp;
                      {new Date(order.created_at).toLocaleDateString()}
                    </div>

                    {actions.length > 0 && (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {actions.map((a) => (
                          <button
                            key={a.label}
                            disabled={actionLoading === order.order_id}
                            onClick={() => handleAction(a.fn, order.order_id, a.label)}
                            style={{
                              padding: '0.35rem 0.8rem', borderRadius: '0.4rem', border: 'none',
                              background: a.color, color: '#fff', fontWeight: 600, fontSize: '0.8rem',
                              cursor: 'pointer', opacity: actionLoading === order.order_id ? 0.5 : 1,
                            }}
                          >
                            {actionLoading === order.order_id ? '...' : a.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
